import { createContext, useMemo } from 'react'

import { useAuth, useFirstVisit } from '@cibo/profile'
import { useProgramStats, useWorkflows } from '@cibo/programs'
import { useFieldStatusCounts } from './useFieldStatusCounts'

export const OnboardingContext = createContext<any>({ fieldCounts: {} })

// if you are visiting within 2 hours of your first login, consider it the first login
const FIRST_VISIT_WINDOW_MS = 1000 * 60 * 60 * 2

export const OnboardingDisplayStateProvider: React.FC<{}> = ({ children }) => {
  const firstVisit = useFirstVisit()
  const { persona, userId } = useAuth()
  const enrollments = useWorkflows(0, 1000, {
    benchmark: ['enrollment'],
  })

  const {
    allFields,
    sensing,
    disqualifiedFields,
    needsInfoFields,
    eligible,
    ...countsStatus
  } = useFieldStatusCounts()

  const stats = useProgramStats({})
  const isFirstVisit = useMemo(() => {
    if (firstVisit.isFetched) {
      if (firstVisit.data) {
        const login = new Date(firstVisit.data)
        const now = new Date()
        return now.valueOf() - login.valueOf() < FIRST_VISIT_WINDOW_MS
      }
    }
    return false
  }, [firstVisit.isFetched, firstVisit.data])

  const isOnboarding = () => {
    switch (persona) {
      case 'admin':
        return false
      case 'advisor':
        return allFields.data ? !allFields.data.numAvailable : true
      case 'grower':
        return !!isFirstVisit || !stats.data?.enrolledFields

      default:
        return true
    }
  }

  return (
    <OnboardingContext.Provider
      value={{
        firstVisit: isFirstVisit,
        isLoading:
          countsStatus.isLoading ||
          enrollments.isLoading ||
          firstVisit.isLoading ||
          stats.isLoading,
        isError: countsStatus.isError || enrollments.isError || firstVisit.isError || stats.isError,
        error: countsStatus.error || enrollments.error || firstVisit.error || stats.error,

        rewardablePrograms: [],
        enrollments: enrollments.data?.items?.filter(({ ownedBy }) => ownedBy.userId === userId),

        fieldCounts: {
          total: allFields.data?.numAvailable ?? 0,
          disqualifed: disqualifiedFields.data?.numAvailable,
          enrolled: stats.data?.enrolledFields,
          eligible: eligible.data?.numAvailable ?? 0,
          sensing: sensing.data?.numAvailable ?? 0,
          unprofiled: needsInfoFields.data?.numAvailable,
        },
        enrolledProgramCount: stats.data?.enrolledFields,
        stats: stats.data,
        isOnboarding: isOnboarding(),
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}
